import { watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserSettingsStore } from '@/stores';
import type { ColorMode } from '@/models/frontend/UserSettings';

const COLOR_MODE_STORAGE_KEY = 'color-mode';

let mediaQuery: MediaQueryList | null = null;
let mediaHandler: ((e: MediaQueryListEvent) => void) | null = null;

function applyTheme(mode: 'light' | 'dark') {
  document.documentElement.setAttribute('data-bs-theme', mode);
}

function getSystemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readStoredColorMode(): ColorMode {
  const stored = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
}

function resolveAndApply(colorMode: ColorMode) {
  // Clean up previous system listener
  if (mediaQuery && mediaHandler) {
    mediaQuery.removeEventListener('change', mediaHandler);
    mediaHandler = null;
  }

  if (colorMode === 'system') {
    applyTheme(getSystemTheme());

    // Listen for system changes
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaHandler = (e: MediaQueryListEvent) => applyTheme(e.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', mediaHandler);
  } else {
    applyTheme(colorMode);
  }
}

export function initColorMode() {
  const userSettingsStore = useUserSettingsStore();
  const { data: userSettings } = storeToRefs(userSettingsStore);

  // Apply immediately from cache so the theme survives a reload before the
  // user settings have been fetched from the API.
  const cachedMode = readStoredColorMode();
  resolveAndApply(cachedMode);
  if (userSettings.value.colorMode !== cachedMode) {
    userSettings.value.colorMode = cachedMode;
  }

  watch(() => userSettings.value.colorMode, (newMode) => {
    localStorage.setItem(COLOR_MODE_STORAGE_KEY, newMode);
    resolveAndApply(newMode);
  });

  // Force light theme for print media
  window.addEventListener('beforeprint', () => {
    applyTheme('light');
  });
  window.addEventListener('afterprint', () => {
    resolveAndApply(userSettings.value.colorMode);
  });
}
